import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_sriman-enterprise/artifacts/ng31c679_image.png";

const links = [
  { to: "/", label: "Home", testId: "nav-home-link" },
  { to: "/services", label: "Services", testId: "nav-services-link" },
  { to: "/products", label: "Products", testId: "nav-products-link" },
  { to: "/about", label: "About", testId: "nav-about-link" },
  { to: "/careers", label: "Careers", testId: "nav-careers-link" },
  { to: "/contact", label: "Contact", testId: "nav-contact-link" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#040812]/85 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center gap-3 group"
        >
          <span className="relative h-10 w-10 shrink-0">
            <img
              src={LOGO_URL}
              alt="Sriman Solutions"
              className="h-full w-full object-contain"
            />
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-display text-white text-[15px] tracking-tight">
              Sriman Solutions
            </span>
            <span className="font-mono text-[10px] text-slate-500 tracking-[0.28em] uppercase mt-1">
              Grow Together
            </span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testId}
              end={l.to === "/"}
              className={({ isActive }) =>
                `px-4 py-2 text-sm font-body transition-colors duration-200 ${
                  isActive
                    ? "text-white"
                    : "text-slate-400 hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:block">
          <Link
            to="/contact"
            data-testid="nav-cta-button"
            className="inline-flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium px-5 py-2.5 rounded-sm transition-colors duration-200"
          >
            Schedule a Consultation
          </Link>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden text-white p-2 -mr-2"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-panel"
          className="md:hidden border-t border-white/10 bg-[#040812]/95 backdrop-blur-xl"
        >
          <div className="px-6 py-6 flex flex-col gap-2">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`${l.testId}-mobile`}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `py-3 border-b border-white/5 text-base font-body ${
                    isActive ? "text-white" : "text-slate-400"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="nav-cta-button-mobile"
              className="mt-4 inline-flex justify-center items-center bg-red-500 hover:bg-red-600 text-white text-sm font-medium px-5 py-3 rounded-sm"
            >
              Schedule a Consultation
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
